import { Type } from '@angular/core';
import { TNil } from '@app/app.interface';
import { AudioTrackingComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/audio-tracking/audio-tracking.component';
import { CaptionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/caption/caption.component';
import { ChatbotMultiComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot-multi/chatbot-multi.component';
import { ChatbotComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot/chatbot.component';
import { ContentComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/content/content.component';
import { CustomComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/custom/custom.component';
import { DescriptionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/description/description.component';
import { GamePlayComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.component';
import { HashtagComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/hashtag/hashtag.component';
import { MentionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/mention/mention.component';
import { MessageComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/message/message.component';
import { ProductPlacementComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/product-placement/product-placement.component';
import { ShowcaseComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/showcase/showcase.component';
import { TalkingPointAiComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/talking-point-ai/talking-point-ai.component';
import { TalkingPointComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/talking-point/talking-point.component';
import { TextTrackingComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/text-tracking/text-tracking.component';
import { TitleComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/title/title.component';
import { VisualComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/visual/visual.component';
import { EDeliverableType } from '@enums/campaign.enum';
import { IDeliverableCard } from '../cta-analytics-details-deliverable-card/cta-analytics-details-deliverable-card.interface';

export type TDeliverableComponent =
  | TitleComponent
  | GamePlayComponent
  | ChatbotComponent
  | ChatbotMultiComponent
  | TalkingPointComponent
  | TalkingPointAiComponent
  | VisualComponent
  | CustomComponent
  | ProductPlacementComponent
  | ShowcaseComponent
  | TextTrackingComponent
  | AudioTrackingComponent
  | CaptionComponent
  | HashtagComponent
  | MentionComponent
  | MessageComponent
  | DescriptionComponent
  | ContentComponent;

export const DELIVERABLE_COMPONENT_MAP: Record<EDeliverableType, Type<TDeliverableComponent>> = {
  [EDeliverableType.title]: TitleComponent,
  [EDeliverableType.gamePlay]: GamePlayComponent,
  [EDeliverableType.chatBot]: ChatbotComponent,
  [EDeliverableType.chatbotMulti]: ChatbotMultiComponent,
  [EDeliverableType.talkingPoint]: TalkingPointComponent,
  [EDeliverableType.talkingPointAi]: TalkingPointAiComponent,
  [EDeliverableType.visual]: VisualComponent,
  [EDeliverableType.productPlacement]: ProductPlacementComponent,
  [EDeliverableType.showcase]: ShowcaseComponent,
  [EDeliverableType.textTracking]: TextTrackingComponent,
  [EDeliverableType.audioTracking]: AudioTrackingComponent,
  [EDeliverableType.caption]: CaptionComponent,
  [EDeliverableType.hashTag]: HashtagComponent,
  [EDeliverableType.mention]: MentionComponent,
  [EDeliverableType.message]: MessageComponent,
  [EDeliverableType.description]: DescriptionComponent,
  [EDeliverableType.content]: ContentComponent,
  [EDeliverableType.custom]: CustomComponent,
};

export const DELIVERABLE_PROCESSING_TIME_MAP: Record<EDeliverableType, string> = {
  [EDeliverableType.title]: 'shared.processingTime.nearTime',
  [EDeliverableType.gamePlay]: 'shared.processingTime.nearTime',
  [EDeliverableType.chatBot]: 'shared.processingTime.nearTime',
  [EDeliverableType.chatbotMulti]: 'shared.processingTime.nearTime',
  [EDeliverableType.talkingPoint]: 'shared.processingTime.hours',
  [EDeliverableType.talkingPointAi]: 'shared.processingTime.hours',
  [EDeliverableType.visual]: 'shared.processingTime.oneToThreeDays',
  [EDeliverableType.productPlacement]: 'shared.processingTime.oneToThreeDays',
  [EDeliverableType.showcase]: 'shared.processingTime.oneToThreeDays',
  [EDeliverableType.textTracking]: 'shared.processingTime.nearTime',
  [EDeliverableType.audioTracking]: 'shared.processingTime.nearTime',
  [EDeliverableType.caption]: 'shared.processingTime.nearTime',
  [EDeliverableType.hashTag]: 'shared.processingTime.nearTime',
  [EDeliverableType.mention]: 'shared.processingTime.nearTime',
  [EDeliverableType.message]: 'shared.processingTime.nearTime',
  [EDeliverableType.description]: 'shared.processingTime.hours',
  [EDeliverableType.content]: 'shared.processingTime.oneToThreeDays',
  [EDeliverableType.custom]: 'shared.processingTime.none',
};

export interface IFormattedKeywordAnalytics {
  names: string[];
  texts: string[];
  totalEntriesCount: number;
}

export type TSharedCardData = Pick<
  IDeliverableCard,
  | 'component'
  | 'lastChanged'
  | 'lastProcessed'
  | 'processingTime'
  | 'shortInfo'
  | 'title'
  | 'titleTooltip'
  | 'type'
  | 'statusManual'
  | 'campaignId'
  | 'campaignStatus'
  | 'campaignProcessStatus'
  | 'creatorId'
  | 'ctaTemplateId'
  | 'socialContentId'
  | 'sponsoredContentRelevant'
>;

export interface ICalculationConfiguration {
  icon?: string | TNil;
  ignoreExpectedKpi?: boolean;
  includeKpisWithNullValue?: boolean;
}
